import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../hooks/useAppSelector";

const ProtectedRoute = ({ children, allowedRoles, isPublic = false }) => {
  const location = useLocation();

  const { user, role, isAuthenticated, authInitialized } = useAppSelector(
    (state) => state.user,
  );

  // =========================================================
  // IMPORTANT:
  // HttpOnly cookie architecture
  //
  // DO NOT check token here.
  //
  // Access token is stored inside HttpOnly cookie and
  // JavaScript cannot read it.
  // =========================================================

  const isLoggedIn = isAuthenticated === true;

  // =========================================================
  // AUTH INITIALIZATION
  //
  // While App.js is checking /users/Me after page refresh,
  // don't redirect to login yet.
  // =========================================================

  if (!authInitialized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // =========================================================
  // PUBLIC ROUTES
  // =========================================================

  if (isPublic) {
    if (isLoggedIn) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  }

  // =========================================================
  // PROTECTED ROUTES
  // =========================================================

  if (!isLoggedIn) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // =========================================================
  // ROLE AUTHORIZATION
  // =========================================================

  if (allowedRoles && allowedRoles.length > 0) {
    const rolesArray = Array.isArray(allowedRoles)
      ? allowedRoles
      : [allowedRoles];

    let userRoles = [];

    // Your API returns:
    //
    // roles: [
    //   {
    //      roleId: 1,
    //      roleName: "SuperAdmin"
    //   }
    // ]
    //
    if (Array.isArray(user?.roles)) {
      userRoles = user.roles.map((x) => x.roleName).filter(Boolean);
    }

    // Also support old single-role state
    if (role) {
      userRoles.push(role);
    }

    const hasPermission = rolesArray.some((allowedRole) =>
      userRoles.includes(allowedRole),
    );

    if (!hasPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // =========================================================
  // AUTHORIZED
  // =========================================================

  return children;
};

export default ProtectedRoute;
